// ParameterLibraryPanel — read-only reference library of literature
// material parameters (absorbers / buffers / ETLs / HTLs / TCOs).
//
// Data comes from GET /simulation/parameter-library (backend branch
// feat/param-library-data). Auth rides the app-wide axios request
// interceptor installed in supabase.js, same as every other call.
// Purely additive tab — publishes a 'parameter_library' slice to the
// WorkspaceContext so the AI chat knows what the user is browsing.
//
// v1.1 (library v1.1): ONE common numbered bibliography for the whole
// library (`library.references`, [Rnnn]); every parameter / J0 channel /
// defect row cites it via `citations: [{ref, note}]`. Citation numbers
// are clickable → a popup with the full bibliographic entry (title,
// journal, DOI link, why it is cited here, where else it is cited);
// each material view ends with its "References cited" list and the
// whole bibliography can be browsed. Legacy `refs` strings still render
// when a library without `references` is served.

import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { themeColors } from '../theme';
import { WorkspaceContextSetter } from '../context/WorkspaceContext';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1`;

// ─── Formatting helpers ───────────────────────────────────────────────────

// Sane numeric display: scientific for very large/small magnitudes
// (2e-10, 1.5e19), plain otherwise (0.35, 300). '—' for null/absent.
export function fmtVal(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  if (n === 0) return '0';
  const a = Math.abs(n);
  if (a >= 1e4 || a < 1e-2) {
    // "2.000e-10" → "2e-10", "2.500e-10" → "2.5e-10", "1e+19" → "1e19"
    return n.toExponential(3)
      .replace(/\.?0+e/, 'e')
      .replace('e+', 'e');
  }
  return String(Number(n.toPrecision(4)));
}

// Copy-friendly form: same scientific-notation policy as fmtVal but with
// NO rounding, so the clipboard value is exact ("2e14", "2.5e-10", "0.35").
export function fmtCopy(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  if (n === 0) return '0';
  const a = Math.abs(n);
  if (a >= 1e4 || a < 1e-2) return n.toExponential().replace('e+', 'e');
  return String(n);
}

// Clipboard with an execCommand fallback for non-secure contexts, older
// browsers, and cases where the async API rejects (e.g. document unfocused).
function copyTextFallback(text) {
  return new Promise((resolve, reject) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
      if (document.execCommand('copy')) resolve();
      else reject(new Error('execCommand copy failed'));
    } catch (e) {
      reject(e);
    } finally {
      document.body.removeChild(ta);
    }
  });
}

function copyText(text) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text)
      .catch(() => copyTextFallback(text));
  }
  return copyTextFallback(text);
}

// Map a library defect entry onto the INTERNAL layer-trap row shape
// ({type, Et, Et_ref, sigma_n, sigma_p, Nt}) used by
// DeviceArchitectureModal.addTrap / PLAnalysisPanel.applyQueue and read
// by serializeLayer (SimulatorPanel). Library defects carry Et_eV +
// Et_reference ('from_Ev' | 'from_Ec') and a free-form type string;
// capture cross-sections and densities are not tabulated in the library,
// so literature-typical fallbacks apply (σ 1e-15 cm², Nt 1e14 cm⁻³).
// Non-simple types (amphoteric, DAP, …) default to 'acceptor'.
export function defectToTrapRow(d) {
  return {
    type: String(d?.type || '').toLowerCase() === 'donor' ? 'donor' : 'acceptor',
    Et: Number.isFinite(Number(d?.Et_eV)) ? Number(d.Et_eV) : 0,
    Et_ref: String(d?.Et_reference || '').toLowerCase().includes('ec') ? 'Ec' : 'Ev',
    sigma_n: d?.sigma_n ?? d?.sigma_n_cm2 ?? 1e-15,
    sigma_p: d?.sigma_p ?? d?.sigma_p_cm2 ?? 1e-15,
    Nt: d?.Nt ?? d?.Nt_cm3 ?? d?.Nt_typical_cm3 ?? 1e14,
  };
}

// ─── Small presentational pieces ─────────────────────────────────────────

// ─── Bibliography helpers (library v1.1) ─────────────────────────────

// Human-readable one-liner for a reference entry. Prefers the Crossref-
// verified fields; falls back to the recorded citation string.
export function formatReference(ref) {
  if (!ref) return '';
  if (ref.type === 'internal' || !ref.verified) return ref.citation || '';
  const parts = [];
  if (ref.authors && ref.authors.length) {
    const a = ref.authors;
    parts.push(a.length > 3 ? `${a[0]} et al.` : a.join(', '));
  }
  if (ref.title) parts.push(`“${ref.title}”`);
  const j = [ref.journal, ref.volume, ref.pages].filter(Boolean).join(' ');
  if (j) parts.push(j + (ref.year ? ` (${ref.year})` : ''));
  else if (ref.year) parts.push(`(${ref.year})`);
  return parts.join(', ') || ref.citation || '';
}

// Index every citation of the library: refId → [{category, material,
// materialLabel, section, rowLabel, note}]. Powers "where else is this
// cited" in the popup and the per-material reference lists.
export function buildCitationIndex(library) {
  const idx = new Map();
  (library?.categories || []).forEach(cat => {
    (cat.materials || []).forEach(mat => {
      [['parameters', 'Parameter'], ['j0_channels', 'J0 channel'], ['defects', 'Defect']]
        .forEach(([sec, secLabel]) => {
          (mat[sec] || []).forEach(row => {
            (row.citations || []).forEach(cit => {
              if (!cit?.ref) return;
              if (!idx.has(cit.ref)) idx.set(cit.ref, []);
              idx.get(cit.ref).push({
                category: cat.id, categoryLabel: cat.label,
                material: mat.id, materialLabel: mat.label,
                section: secLabel, rowLabel: row.label || row.key || row.id,
                note: cit.note || null,
              });
            });
          });
        });
    });
  });
  return idx;
}

// Numbered citation chips for a row. Library v1.1 rows carry
// `citations` (→ common bibliography numbers, click → popup); older
// libraries only carry `refs` strings (→ hover tooltip, as before).
function CitationCell({ row, refMap, onOpen, c }) {
  const cits = (row?.citations || []).filter(x => x?.ref && refMap?.get(x.ref));
  if (cits.length === 0) {
    const refs = row?.refs || [];
    if (!refs.length) return <span style={{ color: c.textDim }}>—</span>;
    const tooltip = refs.map((r, i) => `[${i + 1}] ${r}`).join('\n');
    return (
      <sup title={tooltip}
        style={{ color: c.accent, cursor: 'help', fontSize: 10, letterSpacing: 1 }}>
        {refs.map((_, i) => i + 1).join(',')}
      </sup>
    );
  }
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      {cits.map((cit, i) => {
        const ref = refMap.get(cit.ref);
        return (
          <button
            key={`${cit.ref}-${i}`}
            onClick={() => onOpen(cit.ref, cit.note || null)}
            title={`[${ref.n}] ${ref.citation}${cit.note ? `\n— ${cit.note}` : ''}\n(click for the full reference)`}
            style={{
              background: 'transparent', border: `1px solid ${c.accent}55`,
              color: c.accent, borderRadius: 3, padding: '0 5px', margin: '0 2px 2px 0',
              fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', cursor: 'pointer',
              lineHeight: '15px', verticalAlign: 'middle',
            }}
          >
            {ref.n}
          </button>
        );
      })}
    </span>
  );
}

// Popup with the full bibliographic entry for one reference.
function ReferencePopup({ refId, note, refMap, citIndex, onClose, onJump, c }) {
  const ref = refMap.get(refId);
  const cited = citIndex.get(refId) || [];
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!ref) return null;
  const mono = 'IBM Plex Mono, monospace';
  const link = (href, label) => (
    <a href={href} target="_blank" rel="noopener noreferrer"
      style={{ color: c.accent, textDecoration: 'underline' }}>{label}</a>
  );
  const rowStyle = { display: 'grid', gridTemplateColumns: '92px 1fr', gap: 8, fontSize: 12, marginBottom: 6 };
  const k = { color: c.textDim, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10, paddingTop: 2 };
  // group "where cited" by material
  const byMat = new Map();
  cited.forEach(x => {
    const key = `${x.categoryLabel} › ${x.materialLabel}`;
    if (!byMat.has(key)) byMat.set(key, { material: x.material, category: x.category, rows: [] });
    byMat.get(key).rows.push(x);
  });
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: c.bgCard || c.bg, color: c.text, border: `1px solid ${c.border}`,
        borderRadius: 6, width: 'min(680px, 96vw)', maxHeight: '86vh', overflowY: 'auto',
        padding: '16px 18px', fontFamily: mono, boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
          <span style={{ fontSize: 15, color: c.accentDark, letterSpacing: 1 }}>Reference [{ref.n}]</span>
          <span style={{ fontSize: 10, color: c.textDim, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {ref.type}
            {ref.verified
              ? (ref.verified_by === 'crossref-weak'
                ? ' · probable Crossref match (author/year/journal + topic) — verify'
                : ' · Crossref-verified')
              : (ref.type === 'internal' ? '' : ' · as recorded (not verified)')}
          </span>
          <button onClick={onClose} title="Close (Esc)" style={{
            marginLeft: 'auto', background: 'transparent', border: `1px solid ${c.border}`,
            color: c.textMuted, borderRadius: 3, cursor: 'pointer', padding: '2px 8px', fontFamily: mono,
          }}>✕</button>
        </div>

        <div style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 12, color: c.text }}>
          {formatReference(ref)}
        </div>

        {ref.verified && ref.citation && (
          <div style={rowStyle}><span style={k}>as recorded</span><span style={{ color: c.textMuted }}>{ref.citation}</span></div>
        )}
        {ref.authors && ref.authors.length > 0 && (
          <div style={rowStyle}><span style={k}>authors</span><span>{ref.authors.join('; ')}</span></div>
        )}
        {ref.title && <div style={rowStyle}><span style={k}>title</span><span>{ref.title}</span></div>}
        {(ref.journal || ref.volume || ref.pages || ref.year) && (
          <div style={rowStyle}><span style={k}>published</span>
            <span>{[ref.journal, ref.volume, ref.pages].filter(Boolean).join(' ')}{ref.year ? ` (${ref.year})` : ''}</span></div>
        )}
        {ref.doi && (
          <div style={rowStyle}><span style={k}>DOI</span>
            <span>{link(ref.url || `https://doi.org/${ref.doi}`, ref.doi)}</span></div>
        )}
        {!ref.doi && ref.search_url && ref.type !== 'internal' && (
          <div style={rowStyle}><span style={k}>find it</span>
            <span>{link(ref.search_url, 'search Google Scholar for this citation')}</span></div>
        )}
        {ref.note && <div style={rowStyle}><span style={k}>note</span><span style={{ color: c.textMuted }}>{ref.note}</span></div>}
        {ref.variants && ref.variants.length > 1 && (
          <div style={rowStyle}><span style={k}>also recorded as</span>
            <span style={{ color: c.textDim, fontSize: 11 }}>{ref.variants.join(' · ')}</span></div>
        )}
        {note && (
          <div style={{ ...rowStyle, marginTop: 8 }}><span style={{ ...k, color: c.accent }}>cited here for</span>
            <span style={{ color: c.text }}>{note}</span></div>
        )}

        <div style={{ marginTop: 12, fontSize: 10, color: c.textDim, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Cited {cited.length} time{cited.length !== 1 ? 's' : ''} in the library
        </div>
        <div style={{ fontSize: 12, marginTop: 4, maxHeight: 220, overflowY: 'auto' }}>
          {[...byMat.entries()].map(([label, grp]) => (
            <div key={label} style={{ marginBottom: 6 }}>
              <button onClick={() => { onJump(grp.category, grp.material); onClose(); }}
                title="Open this material" style={{
                  background: 'transparent', border: 'none', color: c.accent, cursor: 'pointer',
                  padding: 0, fontFamily: mono, fontSize: 12, textDecoration: 'underline',
                }}>{label}</button>
              <span style={{ color: c.textMuted }}>
                {' — '}{grp.rows.map(r => `${r.section.toLowerCase()} ${r.rowLabel}`).join(', ')}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Per-material "References cited" list (numbered, full entries) with a
// toggle to browse the entire common bibliography.
function ReferencesSection({ material, refMap, onOpen, showAll, onToggleAll, allRefs, c }) {
  const ids = new Set();
  ['parameters', 'j0_channels', 'defects'].forEach(sec => {
    (material?.[sec] || []).forEach(row => (row.citations || []).forEach(x => x?.ref && ids.add(x.ref)));
  });
  const cited = [...ids].map(id => refMap.get(id)).filter(Boolean).sort((a, b) => a.n - b.n);
  const list = showAll ? allRefs : cited;
  const mono = 'IBM Plex Mono, monospace';
  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{ margin: '0 0 2px', fontSize: 14, color: c.accentDark, letterSpacing: 1 }}>
          {showAll ? `Common bibliography — ${allRefs.length} references` : `References cited — ${material?.label} (${cited.length})`}
        </h3>
        <button onClick={onToggleAll} style={{
          background: c.btnSecondary, color: c.btnSecondaryText, border: `1px solid ${c.border}`,
          borderRadius: 3, padding: '2px 8px', fontSize: 11, fontFamily: mono, cursor: 'pointer',
        }}>
          {showAll ? '← this material only' : `browse full library (${allRefs.length})`}
        </button>
      </div>
      <div style={{ fontSize: 11, color: c.textMuted, fontStyle: 'italic', margin: '2px 0 8px', lineHeight: 1.5 }}>
        Numbers are shared across the whole library — the same [n] means the
        same source on every material page. Click a number for the full
        entry, DOI link and every place it is cited.
      </div>
      <div style={{ border: `1px solid ${c.border}`, borderRadius: 4, padding: '6px 10px' }}>
        {list.length === 0 && <div style={{ fontSize: 12, color: c.textDim }}>No references.</div>}
        {list.map(ref => (
          <div key={ref.id} style={{ display: 'grid', gridTemplateColumns: '44px 1fr', gap: 8, padding: '4px 0', borderBottom: `1px solid ${c.border}22`, fontSize: 12 }}>
            <button onClick={() => onOpen(ref.id, null)} title="Open reference" style={{
              background: 'transparent', border: `1px solid ${c.accent}55`, color: c.accent, borderRadius: 3,
              cursor: 'pointer', fontFamily: mono, fontSize: 11, height: 20, alignSelf: 'start',
            }}>[{ref.n}]</button>
            <div style={{ lineHeight: 1.5 }}>
              <span>{formatReference(ref)}</span>
              {ref.doi && (
                <>{' '}<a href={ref.url || `https://doi.org/${ref.doi}`} target="_blank" rel="noopener noreferrer"
                  style={{ color: c.accent, fontSize: 11 }}>doi:{ref.doi}</a></>
              )}
              {ref.type === 'internal' && <span style={{ color: c.textDim, fontSize: 11 }}> (project-internal source)</span>}
              {!ref.verified && ref.type !== 'internal' && <span style={{ color: c.textDim, fontSize: 11 }}> (as recorded)</span>}
              {ref.verified && ref.verified_by === 'crossref-weak' && <span style={{ color: c.textDim, fontSize: 11 }}> (probable match)</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Notes: truncated to one line by default. Full text on hover (title)
// AND click-to-expand — clicking toggles the cell between the truncated
// single line and the full wrapped note (the row grows to fit).
function NotesCell({ notes, c }) {
  const [expanded, setExpanded] = useState(false);
  if (!notes) return <span style={{ color: c.textDim }}>—</span>;
  return (
    <span
      title={expanded ? `${notes}\n\n(click to collapse)` : notes}
      onClick={() => setExpanded(x => !x)}
      style={expanded ? {
        display: 'inline-block', maxWidth: 360, whiteSpace: 'normal',
        lineHeight: 1.5, cursor: 'pointer', wordBreak: 'break-word',
      } : {
        display: 'inline-block', maxWidth: 240, whiteSpace: 'nowrap',
        overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom',
        cursor: 'pointer',
      }}
    >
      {notes}
    </span>
  );
}

function CopyButton({ value, c }) {
  const [copied, setCopied] = useState(false);
  const disabled = value == null || value === '';
  return (
    <button
      disabled={disabled}
      title={disabled ? 'No typical value' : `Copy typical value (${fmtCopy(value)})`}
      onClick={() => {
        copyText(fmtCopy(value)).then(
          () => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          },
          () => {},
        );
      }}
      style={{
        background: copied ? c.green : c.btnSecondary,
        color: copied ? '#000' : c.btnSecondaryText,
        border: `1px solid ${copied ? c.green : c.border}`,
        borderRadius: 3, padding: '2px 8px', fontSize: 11,
        fontFamily: 'IBM Plex Mono, monospace',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {copied ? '✓' : 'Copy'}
    </button>
  );
}

function RangeCell({ row, c }) {
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      {fmtVal(row.min)}
      <span style={{ color: c.textDim }}> – </span>
      <strong style={{ color: c.text }}>{fmtVal(row.typical)}</strong>
      <span style={{ color: c.textDim }}> – </span>
      {fmtVal(row.max)}
    </span>
  );
}

// ─── Component ────────────────────────────────────────────────────────────

export default function ParameterLibraryPanel({
  theme = 'dark',
  onAddDefectToAbsorber = null,
}) {
  const c = themeColors(theme);

  const [library, setLibrary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  const [catId, setCatId] = useState('absorbers');
  const [matId, setMatId] = useState('CdTe');

  // Bibliography (library v1.1): id → reference, citation index, popup.
  const refMap = React.useMemo(() => {
    const m = new Map();
    (library?.references || []).forEach(r => m.set(r.id, r));
    return m;
  }, [library]);
  const citIndex = React.useMemo(() => buildCitationIndex(library), [library]);
  const [popup, setPopup] = useState(null);           // {refId, note}
  const [showAllRefs, setShowAllRefs] = useState(false);
  const openRef = useCallback((refId, note) => setPopup({ refId, note }), []);
  const closeRef = useCallback(() => setPopup(null), []);
  const jumpTo = useCallback((cat, mat) => { setCatId(cat); setMatId(mat); }, []);

  // ── Fetch (auth via the app-wide axios interceptor) ─────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFetchError(null);
    axios.get(`${API}/simulation/parameter-library`)
      .then(({ data }) => {
        if (cancelled) return;
        setLibrary(data);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setFetchError(
          e?.response?.status
            ? `Server returned ${e.response.status}`
            : (e?.message || 'Network error'),
        );
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [retryKey]);

  const retry = useCallback(() => setRetryKey(k => k + 1), []);

  // ── Defect → absorber integration ───────────────────────────────────
  // Brief confirmation after "Add to absorber" (auto-clears). ok=false
  // when the device has no absorber layer yet.
  const [defectNotice, setDefectNotice] = useState(null); // {ok, text}
  const noticeTimer = useRef(null);
  useEffect(() => () => {
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
  }, []);

  function handleAddDefect(d) {
    if (!onAddDefectToAbsorber) return;
    const layerName = onAddDefectToAbsorber(defectToTrapRow(d));
    const notice = layerName
      ? {
          ok: true,
          text: `Added ${d.label || d.id} to the ${layerName} absorber as a `
            + `bulk trap — visible in Define Device › Defects and included `
            + `in the next simulation.`,
        }
      : {
          ok: false,
          text: 'No absorber layer defined — add one in Define Device first.',
        };
    setDefectNotice(notice);
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setDefectNotice(null), 5000);
  }

  // ── Selection resolution ─────────────────────────────────────────────
  const categories = library?.categories || [];
  const category = categories.find(x => x.id === catId) || null;
  const materials = category?.materials || [];
  const material = materials.find(x => x.id === matId) || null;

  // Snap selections onto valid entries once (or whenever) the library
  // loads — default absorbers/CdTe when available.
  useEffect(() => {
    if (!library?.categories?.length) return;
    const cats = library.categories;
    setCatId(prev => {
      if (cats.some(x => x.id === prev)) return prev;
      return cats.some(x => x.id === 'absorbers') ? 'absorbers' : cats[0].id;
    });
  }, [library]);

  useEffect(() => {
    if (!category) return;
    const mats = category.materials || [];
    if (!mats.length) return;
    setMatId(prev => {
      if (mats.some(x => x.id === prev)) return prev;
      return mats.some(x => x.id === 'CdTe') ? 'CdTe' : mats[0].id;
    });
  }, [category]);

  // ── Stage 2: publish to WorkspaceContext (parameter_library block) ──
  const publish = useContext(WorkspaceContextSetter);
  useEffect(() => {
    publish('parameter_library', {
      status: 'idle',   // overwrite the provider's 'context_pending' seed
      category: category?.id || null,
      category_label: category?.label || null,
      material: material?.id || null,
      material_label: material?.label || null,
      library_loaded: !!library,
      fetch_error: fetchError,
    });
  }, [publish, category, material, library, fetchError]);

  // ── Shared styles ────────────────────────────────────────────────────
  const mono = 'IBM Plex Mono, monospace';
  const selectStyle = {
    background: c.input, color: c.inputText,
    border: `1px solid ${c.border}`, borderRadius: 4,
    padding: '5px 8px', fontFamily: mono, fontSize: 12, cursor: 'pointer',
  };
  const labelStyle = {
    fontSize: 11, color: c.textMuted, textTransform: 'uppercase',
    letterSpacing: 0.5, display: 'flex', flexDirection: 'column', gap: 4,
  };
  const thStyle = {
    textAlign: 'left', padding: '6px 10px', fontSize: 11,
    color: c.textMuted, textTransform: 'uppercase', letterSpacing: 0.5,
    borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap',
  };
  const tdStyle = {
    padding: '6px 10px', fontSize: 12, color: c.text,
    borderBottom: `1px solid ${c.border}`, verticalAlign: 'top',
  };
  const sectionTitle = {
    margin: '22px 0 2px', fontSize: 14, color: c.accentDark, letterSpacing: 1,
  };
  const captionStyle = {
    fontSize: 11, color: c.textMuted, fontStyle: 'italic',
    margin: '2px 0 8px', lineHeight: 1.5,
  };
  const tableWrap = {
    border: `1px solid ${c.border}`, borderRadius: 4, overflowX: 'auto',
  };

  function renderTable(headers, rows) {
    return (
      <div style={tableWrap}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>{headers.map(h => <th key={h} style={thStyle}>{h}</th>)}</tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div style={{
      padding: 20, color: c.text, background: c.bg,
      height: '100%', width: '100%', overflowY: 'auto',
      fontFamily: mono,
    }}>
      {/* Header */}
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: 16, color: c.accentDark, letterSpacing: 1 }}>
          Parameter Library
        </h2>
        <div style={{ fontSize: 13, color: c.textMuted, marginTop: 4, lineHeight: 1.5 }}>
          Literature ranges for material parameters, J0 recombination
          channels, and defect levels. Read-only reference — copy typical
          values into Define Device.
        </div>
        {library && (
          <div style={{ fontSize: 11, color: c.textDim, marginTop: 6 }}>
            v{library.version || '?'}
            {library.generated ? ` · generated ${library.generated}` : ''}
            {refMap.size > 0 ? ` · ${refMap.size} references in the common bibliography` : ''}
          </div>
        )}
        {library?.disclaimer && (
          <div style={{
            fontSize: 11, color: c.textMuted, fontStyle: 'italic',
            marginTop: 4, lineHeight: 1.5,
          }}>
            {library.disclaimer}
          </div>
        )}
      </div>

      {/* Loading / error states */}
      {loading && (
        <div style={{ fontSize: 13, color: c.textMuted, padding: '20px 0' }}>
          ⏳ Loading parameter library…
        </div>
      )}

      {!loading && fetchError && (
        <div style={{
          padding: 14, border: `1px solid ${c.red}`, borderRadius: 4,
          background: c.isDark ? 'rgba(248,113,113,0.05)' : 'rgba(220,53,69,0.08)',
          maxWidth: 520,
        }}>
          <div style={{ fontSize: 13, color: c.red, marginBottom: 6 }}>
            Library unavailable
          </div>
          <div style={{ fontSize: 12, color: c.textMuted, marginBottom: 10 }}>
            {fetchError}. The parameter library could not be loaded from the
            server.
          </div>
          <button
            onClick={retry}
            style={{
              background: c.btnPrimary, color: c.btnPrimaryText,
              border: `1px solid ${c.btnPrimary}`, borderRadius: 3,
              padding: '6px 16px', fontSize: 12, fontFamily: mono,
              cursor: 'pointer', fontWeight: 500,
            }}
          >
            ↻ Retry
          </button>
        </div>
      )}

      {!loading && !fetchError && library && (
        <>
          {/* Category / material pickers */}
          <div style={{
            display: 'flex', gap: 16, alignItems: 'flex-end',
            flexWrap: 'wrap', marginBottom: 16,
          }}>
            <label style={labelStyle}>
              Category
              <select
                style={selectStyle}
                value={category?.id || ''}
                onChange={e => setCatId(e.target.value)}
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </label>
            <label style={labelStyle}>
              Material
              <select
                style={selectStyle}
                value={material?.id || ''}
                onChange={e => setMatId(e.target.value)}
              >
                {materials.map(m => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </label>
          </div>

          {!material && (
            <div style={{ fontSize: 13, color: c.textMuted }}>
              No materials in this category.
            </div>
          )}

          {material && (
            <>
              {/* ── Parameters ─────────────────────────────────────── */}
              <h3 style={sectionTitle}>Parameters — {material.label}</h3>
              <div style={captionStyle}>
                Range shown as min – <strong>typical</strong> – max. Copy
                puts the typical value on the clipboard for Define Device.
              </div>
              {(material.parameters || []).length === 0 ? (
                <div style={{ fontSize: 12, color: c.textDim }}>
                  No parameters listed.
                </div>
              ) : renderTable(
                ['Parameter', 'Range (min – typical – max)', 'Units', 'Notes', 'Refs', ''],
                (material.parameters || []).map(p => (
                  <tr key={p.key}>
                    <td style={tdStyle}>{p.label || p.key}</td>
                    <td style={tdStyle}><RangeCell row={p} c={c} /></td>
                    <td style={{ ...tdStyle, color: c.textMuted, whiteSpace: 'nowrap' }}>
                      {p.units || '—'}
                    </td>
                    <td style={tdStyle}><NotesCell notes={p.notes} c={c} /></td>
                    <td style={tdStyle}><CitationCell row={p} refMap={refMap} onOpen={openRef} c={c} /></td>
                    <td style={tdStyle}><CopyButton value={p.typical} c={c} /></td>
                  </tr>
                )),
              )}

              {/* ── J0 channels (absorbers only) ───────────────────── */}
              {category?.id === 'absorbers' &&
                (material.j0_channels || []).length > 0 && (
                <>
                  <h3 style={sectionTitle}>J0 Recombination Channels</h3>
                  <div style={captionStyle}>
                    Avarodha what-if starting ranges — paste into the
                    Avarodha tab's J0 overlay editor.
                  </div>
                  {renderTable(
                    ['Channel', 'Range (min – typical – max)', 'Units', 'n (ideality)', 'Notes', 'Refs', ''],
                    material.j0_channels.map(ch => (
                      <tr key={ch.key}>
                        <td style={tdStyle}>{ch.label || ch.key}</td>
                        <td style={tdStyle}><RangeCell row={ch} c={c} /></td>
                        <td style={{ ...tdStyle, color: c.textMuted, whiteSpace: 'nowrap' }}>
                          {ch.units || '—'}
                        </td>
                        <td style={tdStyle}>{fmtVal(ch.n_ideality)}</td>
                        <td style={tdStyle}><NotesCell notes={ch.notes} c={c} /></td>
                        <td style={tdStyle}><CitationCell row={ch} refMap={refMap} onOpen={openRef} c={c} /></td>
                        <td style={tdStyle}><CopyButton value={ch.typical} c={c} /></td>
                      </tr>
                    )),
                  )}
                </>
              )}

              {/* ── Defects ────────────────────────────────────────── */}
              {(material.defects || []).length > 0 && (
                <>
                  <h3 style={sectionTitle}>Defect Levels</h3>
                  <div style={captionStyle}>
                    For the PL/TRPL simulator's trap inputs and PL-Analysis
                    peak identification. Et values are as reported at the
                    listed temperature — bandgap shifts with T.
                    {onAddDefectToAbsorber && (
                      <> "Add to absorber" inserts the defect as a bulk trap
                      in the device's primary (thickest) absorber layer
                      (σ 1e-15 cm² and Nt 1e14 cm⁻³ unless published).</>
                    )}
                  </div>
                  {defectNotice && (
                    <div style={{
                      margin: '0 0 8px', padding: '6px 12px', fontSize: 12,
                      border: `1px solid ${defectNotice.ok ? c.green : c.amber}`,
                      color: defectNotice.ok ? c.green : c.amber,
                      borderRadius: 4,
                    }}>
                      {defectNotice.ok ? '✓ ' : '⚠ '}{defectNotice.text}
                    </div>
                  )}
                  {renderTable(
                    ['Defect', 'Type', 'Et (eV)', 'T reported (K)', 'PL peak (eV)', 'Notes', 'Refs', ''],
                    material.defects.map(d => (
                      <tr key={d.id}>
                        <td style={tdStyle}>{d.label || d.id}</td>
                        <td style={{ ...tdStyle, color: c.textMuted }}>{d.type || '—'}</td>
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                          {fmtVal(d.Et_eV)}
                          {d.Et_reference && (
                            <span style={{ color: c.textDim, fontSize: 11 }}>
                              {' '}({String(d.Et_reference).replace(/_/g, ' ')})
                            </span>
                          )}
                        </td>
                        <td style={tdStyle}>{fmtVal(d.T_K)}</td>
                        <td style={tdStyle}>{fmtVal(d.PL_peak_eV)}</td>
                        <td style={tdStyle}><NotesCell notes={d.notes} c={c} /></td>
                        <td style={tdStyle}><CitationCell row={d} refMap={refMap} onOpen={openRef} c={c} /></td>
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                          {onAddDefectToAbsorber && (
                            <button
                              title={`Insert ${d.label || d.id} as a bulk trap in the primary absorber layer`}
                              onClick={() => handleAddDefect(d)}
                              style={{
                                background: c.btnSecondary,
                                color: c.btnSecondaryText,
                                border: `1px solid ${c.border}`,
                                borderRadius: 3, padding: '2px 8px',
                                fontSize: 11, fontFamily: mono,
                                cursor: 'pointer',
                              }}
                            >
                              + Add to absorber
                            </button>
                          )}
                        </td>
                      </tr>
                    )),
                  )}
                </>
              )}

              {/* ── References (library v1.1 common bibliography) ─── */}
              {refMap.size > 0 && (
                <ReferencesSection
                  material={material} refMap={refMap} onOpen={openRef}
                  showAll={showAllRefs} onToggleAll={() => setShowAllRefs(x => !x)}
                  allRefs={library.references || []} c={c}
                />
              )}

              {/* Bottom breathing room inside the scroll container */}
              <div style={{ height: 24 }} />
            </>
          )}
        </>
      )}

      {popup && refMap.size > 0 && (
        <ReferencePopup
          refId={popup.refId} note={popup.note} refMap={refMap} citIndex={citIndex}
          onClose={closeRef} onJump={jumpTo} c={c}
        />
      )}
    </div>
  );
}
